"use client"

import * as React from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { Check, CreditCard, Minus, Package, Plus, ShieldCheck, Truck } from "lucide-react"

import { Money } from "@/components/shared/money"
import { Stars } from "@/components/shared/stars"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useCart } from "@/features/cart/hooks/use-cart"
import { useCartDrawer } from "@/features/cart/hooks/use-cart-drawer"
import { buyNowHref, useBuySelection } from "@/features/catalog/hooks/use-buy-selection"
import { PincodeCheck } from "@/features/catalog/components/pincode-check"
import { ProductGallery } from "@/features/catalog/components/product-gallery"
import type { Product } from "@/features/catalog/catalog"
import { siteConfig } from "@/lib/config/site"
import { discountPercent } from "@/lib/money"
import { cn } from "@/lib/utils"

const TRUST = [
  { Icon: ShieldCheck, label: "7-day returns" },
  { Icon: Truck, label: "Ships in 48 hours" },
  { Icon: Package, label: "Made in India" },
  { Icon: CreditCard, label: "UPI · Cards · Netbanking" },
]

const MAX_QTY = 9

export function ProductDetail({
  product,
  freeShipping,
  initialColourway,
}: {
  product: Product
  /** No shipping charge anywhere (Settings > Shipping charge at 0%). */
  freeShipping: boolean
  /** From `?colour=` - a lineup card opens this page on the colour clicked. */
  initialColourway?: string
}) {
  // Validated: an unknown ?colour= would leave no selection and no image.
  const [colourwayId, setColourwayId] = React.useState(
    product.colourways.find((c) => c.id === initialColourway)?.id ?? product.colourways[0]!.id,
  )

  // ?colour= is read after mount: reading it on the server, or useSearchParams(), makes the route
  // dynamic.
  React.useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("colour")
    if (!wanted) return
    const match = product.colourways.find((c) => c.id === wanted)
    // After mount on purpose: window.location during render would mismatch hydration.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (match) setColourwayId(match.id)
  }, [product.colourways])
  const [qty, setQty] = React.useState(1)
  // Shared with the phone's sticky bar, so its Buy now and price match this pick.
  const setSelection = useBuySelection((s) => s.set)
  const pickedPrice = product.colourways.find((c) => c.id === colourwayId)?.price ?? product.price
  React.useEffect(
    () => setSelection(colourwayId, qty, pickedPrice, freeShipping),
    [colourwayId, qty, pickedPrice, freeShipping, setSelection],
  )
  const [shot, setShot] = React.useState(0)
  const add = useCart((s) => s.add)
  const showCart = useCartDrawer((s) => s.show)
  const router = useRouter()

  const colourway = product.colourways.find((c) => c.id === colourwayId) ?? product.colourways[0]!

  const gallery = React.useMemo(
    () =>
      product.gallery[colourway.id].map((image) => ({
        ...image,
        alt: `${colourway.name} — ${image.alt}`,
      })),
    [colourway, product.gallery],
  )

  const lineTotal = Number(colourway.price) * qty
  // Checkout refuses a sold-out colourway anyway; say so before anyone tries.
  const soldOut = !colourway.inStock

  // "Added to cart" on the button for a moment after a tap.
  const [added, setAdded] = React.useState(false)
  React.useEffect(() => {
    if (!added) return
    const timer = window.setTimeout(() => setAdded(false), 1800)
    return () => window.clearTimeout(timer)
  }, [added])

  return (
    <div
      id="buy"
      className="grid grid-cols-1 gap-8 px-5 py-8 sm:px-8 lg:grid-cols-[minmax(0,calc(100dvh-19rem))_minmax(0,1fr)] lg:gap-14 lg:py-10 xl:px-14"
    >
      {/* ── Gallery ───────────────────────────────────────────── */}
      <ProductGallery
        images={gallery}
        shot={shot}
        onShot={setShot}
        badges={colourway.inStock ? <Badge variant="solid">In stock</Badge> : null}
      />

      {/* ── Buy panel ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-5">
        <div>
          {/* The count only when real: without a database there is no stock figure. */}
          {!colourway.inStock ? (
            <div className="text-magenta mb-3.5 font-mono text-[11px] tracking-[0.18em] uppercase">
              Sold out in this colourway
            </div>
          ) : colourway.stock > 0 ? (
            <div className="text-acid mb-3.5 flex items-center gap-2.5 font-mono text-[11px] tracking-[0.18em] uppercase">
              <span className="animate-blink bg-acid size-1.5 rounded-full" />
              {colourway.stock} left in this colourway
            </div>
          ) : null}
          <h1 className="font-display text-bone mb-3 text-[38px] leading-[1.04] uppercase sm:text-[46px] lg:text-[40px] xl:text-[54px]">
            {product.name}
          </h1>
          <div className="text-dim flex flex-wrap items-center gap-x-3.5 gap-y-1.5 font-mono text-[11px] tracking-[0.08em] sm:text-xs">
            {/* No stars for a product nobody has reviewed yet. */}
            {product.reviewCount > 0 ? (
              <>
                <Stars rating={product.rating} />
                <span className="text-bone">{product.rating}</span>
                <span>{product.reviewCount} REVIEWS</span>
                <span aria-hidden className="hidden h-3 w-px bg-white/15 sm:block" />
              </>
            ) : null}
            <span>{colourway.sku}</span>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-baseline gap-3">
            <Money
              value={colourway.price}
              className="font-display text-bone text-[38px] leading-[1.04] sm:text-[46px]"
            />
            <Money value={product.compareAtPrice} strike className="text-[17px]" />
            <Badge variant="solid">
              Save {discountPercent(product.compareAtPrice, colourway.price)}%
            </Badge>
          </div>
          <p className="text-dim mt-1.5 text-[12.5px]">
            Inclusive of all taxes · {freeShipping ? "Free shipping" : "Shipping by pincode"}
          </p>
          {/* What the thing is, in the words riders search for. */}
          <p className="text-ash mt-4 max-w-[52ch] text-[14px] leading-[1.6]">{product.intro}</p>
        </div>

        {/* colourway */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <span className="text-dim font-mono text-[11px] tracking-[0.14em] uppercase">
              Colourway
            </span>
            <span className="text-bone text-[13.5px] font-semibold">{colourway.name}</span>
          </div>
          <div className="flex gap-3">
            {product.colourways.map((c) => {
              const selected = c.id === colourwayId
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setColourwayId(c.id)
                    setShot(0)
                  }}
                  aria-label={c.name}
                  aria-pressed={selected}
                  className={cn(
                    "rounded-full border p-1 transition-transform duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-105",
                    selected ? "border-[1.5px]" : "border-white/15",
                  )}
                  style={
                    selected ? { borderColor: c.hex, boxShadow: `0 0 16px ${c.hex}55` } : undefined
                  }
                >
                  {/* The 3D model swatch. For a plain colour circle, swap in the span below. */}
                  <span className="relative block size-14">
                    <Image src={c.swatch} alt="" fill sizes="56px" className="object-contain" />
                  </span>
                  {/* <span className="block size-8 rounded-full" style={{ backgroundColor: c.hex }} /> */}
                </button>
              )
            })}
          </div>
        </div>

        {/* A container query: the column's width depends on screen height too, so no breakpoint
            fits. From 40rem all three share a row; narrower, Buy it now wraps, and under 24rem
            the price leaves Add to cart's label. */}
        <div className="@container flex flex-wrap gap-2.5 xl:max-w-[720px]">
          <div className="flex h-[58px] shrink-0 items-center gap-1 rounded-full border border-white/[0.16] px-1.5">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              aria-label="Decrease quantity"
              disabled={qty <= 1}
              className="text-bone flex size-11 items-center justify-center rounded-full disabled:opacity-35"
            >
              <Minus className="size-4" strokeWidth={2.2} />
            </button>
            <span className="text-bone min-w-7 text-center font-mono text-base font-bold">
              {qty}
            </span>
            <button
              type="button"
              onClick={() => setQty((q) => Math.min(MAX_QTY, q + 1))}
              aria-label="Increase quantity"
              disabled={qty >= MAX_QTY}
              className="text-bone flex size-11 items-center justify-center rounded-full disabled:opacity-35"
            >
              <Plus className="size-4" strokeWidth={2.2} />
            </button>
          </div>

          <Button
            type="button"
            variant="primary"
            size="lg"
            className="min-w-[150px] flex-1 @max-[24rem]:px-5"
            disabled={soldOut}
            onClick={(e) => {
              add(colourwayId, qty, product.slug)
              // Open the cart: the header's count alone is easy to miss on a phone.
              setAdded(true)
              showCart(e.currentTarget)
            }}
          >
            {soldOut ? (
              "Sold out"
            ) : added ? (
              <span className="inline-flex items-center gap-2">
                <Check className="size-4" strokeWidth={2.6} />
                Added to cart
              </span>
            ) : (
              <>
                {/* Price hidden where the row is too narrow; it is right above. */}
                <span>
                  Add to cart<span className="@max-[24rem]:hidden"> ·</span>
                </span>
                <Money value={lineTotal} className="@max-[24rem]:hidden" />
              </>
            )}
          </Button>

          <Button
            variant="accent"
            size="lg"
            className="min-w-0 grow basis-full @min-[40rem]:basis-0"
            disabled={soldOut}
            onClick={() =>
              // Straight to checkout with just this, leaving the cart as it is.
              router.push(buyNowHref(colourwayId, qty, product.slug))
            }
          >
            Buy it now
          </Button>
        </div>

        <PincodeCheck />

        {/* trust */}
        <div className="grid grid-cols-2 gap-2.5">
          {TRUST.map(({ Icon, label }) => (
            <div
              key={label}
              className="bg-carbon flex items-center gap-2.5 rounded-xl border border-white/[0.08] p-3.5"
            >
              <Icon className="text-acid size-4 shrink-0" strokeWidth={1.7} />
              <span className="text-bone text-[12.5px]">{label}</span>
            </div>
          ))}
        </div>

        {/* in the box */}
        <div className="rounded-tile bg-carbon border border-white/[0.09] p-5">
          <div className="text-dim mb-3.5 font-mono text-[11px] tracking-[0.14em] uppercase">
            In the box
          </div>
          <ul className="flex flex-col gap-2.5">
            {product.inTheBox.map((item) => (
              <li key={item} className="text-ash flex items-center gap-2.5 text-[14px]">
                <Check className="text-acid size-4 shrink-0" strokeWidth={2.4} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* What the Legal Metrology (Packaged Commodities) Rules require of an online listing.
            Owner's decision: the maker is named without its address (it is on /contact). */}
        <details className="group rounded-tile bg-carbon border border-white/[0.09] px-5 py-4">
          <summary className="text-dim flex min-h-8 cursor-pointer list-none items-center justify-between font-mono text-[11px] tracking-[0.18em] uppercase">
            Product information
            <Plus
              className="size-4 transition-transform group-open:rotate-45"
              strokeWidth={2}
              aria-hidden
            />
          </summary>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[13px]">
            {[
              ["Product", product.productType],
              ["Net quantity", "1 unit: the mount and its wall fixings"],
              [
                "MRP",
                <>
                  <Money value={product.compareAtPrice} /> (inclusive of all taxes)
                </>,
              ],
              ["Country of origin", "India"],
              ["Manufactured and packed by", siteConfig.legalEntity],
              ["Consumer care", `${siteConfig.supportEmail} · ${siteConfig.phone}`],
            ].map(([label, value]) => (
              <React.Fragment key={String(label)}>
                <dt className="text-dim">{label}</dt>
                <dd className="text-ash">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      </div>
    </div>
  )
}
